import { BigNumber } from 'lib/BigInt'
import {
  faUser,
  faExternalLink,
  faPhotoVideo
} from '@fortawesome/pro-regular-svg-icons'
import Thumbnail from 'components/Thumbnail'
import {
  CollectiveMemberFieldsFragment,
  CollectiveTokenType,
  useBanMemberFromEventsMutation
} from 'graphql/generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRobot } from '@fortawesome/pro-light-svg-icons'
import { useCollectiveContext } from 'context/CollectiveContext'
import { formatToken, getEtherscanUrl } from 'lib/collectives/helpers'
import EtherscanLink from './Common/EtherscanLink'
import { twMerge } from 'tailwind-merge'
import Typography from 'components/common/Typography'
import { useQueryErrorHandler } from 'hooks'
import Button from 'components/Button'

interface IProps {
  member: CollectiveMemberFieldsFragment
}

function CollectiveMemberListItem({ member }: IProps) {
  const { collective } = useCollectiveContext()

  const { user } = member
  const { platformUser } = user

  const onBanError = useQueryErrorHandler('Failed to ban user')
  const [banMember, { loading: banning }] = useBanMemberFromEventsMutation({
    variables: {
      collectiveId: collective.id,
      userId: user.id
    },
    onError: onBanError
  })

  return (
    <div className="sm:text-center bg-gray-50 rounded-lg shadow flex sm:flex-col justify-center">
      <div className="max-sm:basis-1/3 flex flex-col justify-center sm:items-start px-3 sm:pt-6 sm:px-8 xl:pt-8 xl:px-10">
        {platformUser?.thumb ? (
          <Thumbnail
            src={platformUser.thumb}
            useImgTag
            className={'rounded-full overflow-hidden'}
          />
        ) : (
          <div className="aspect-w-1 aspect-h-1 w-full rounded-full bg-gray-200">
            <div
              className={twMerge('flex flex-col justify-center items-center')}
            >
              <FontAwesomeIcon
                icon={platformUser ? faPhotoVideo : faRobot}
                className={twMerge(platformUser && 'text-gray-500')}
                size={'2x'}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col max-sm:basis-2/3 max-sm:border-l justify-end border-gray-200">
        <dl className="flex-1 p-4 flex flex-col">
          <div>
            {!!platformUser && (
              <Typography>{platformUser.displayName}</Typography>
            )}

            <EtherscanLink
              network={collective.network}
              type={'address'}
              address={user.address}
              inline
              shortAddress
            />
          </div>

          <dd className="mt-1">
            {member.privileged ? (
              <span
                className={
                  'px-2 py-1 text-green-800 text-xs font-medium rounded-full bg-green-100'
                }
              >
                {`Signator`}
              </span>
            ) : !member.approved ? (
              <span
                className={
                  'px-2 py-1 text-red-800 text-xs font-medium rounded-full bg-red-100'
                }
              >
                {`Not approved`}
              </span>
            ) : (
              <span className={'py-1'}>&nbsp;</span> // for equal heights in grid
            )}
          </dd>

          {collective.membership?.privileged &&
            (member.bannedFromEvents ? (
              <dd className="mt-1">
                <span className="px-2 py-1 text-red-800 text-xs font-medium rounded-full bg-red-100">
                  {`Banned from Events`}
                </span>
              </dd>
            ) : (
              <dd className="mt-2">
                <Button
                  onClick={() => banMember()}
                  loading={banning}
                  disabled={banning}
                  size="xs"
                  color="outline"
                >
                  Ban from Events
                </Button>
              </dd>
            ))}
        </dl>

        <div className="p-3 border-t border-gray-200">
          {collective.governanceType === CollectiveTokenType.OWNED && (
            <>
              <p className="text-xs font-medium text-gray-500 mb-1">
                Wallet Ownership
              </p>
              <p className="text-sm text-gray-900">
                {`${member.percentOfTotalTokenSupply}% `}
                <br />
                <span className="text-sm text-gray-700 font-light">
                  {formatToken(member.totalTokens, {
                    decimals: collective.tokens[0].decimals,
                    roundToDecimals: 2
                  })}{' '}
                  {collective.tokens[0].symbol}
                </span>
              </p>
            </>
          )}

          {collective.governanceType === CollectiveTokenType.EXTERNAL && (
            <>
              <Typography className="mb-1" color="gray" size="sm">
                Voting Power
              </Typography>
              <Typography size="sm" component={'span'}>
                {`${new BigNumber(member.totalTokens)
                  .times(100)
                  .div(collective.tokens[0].totalVotingSupply)
                  .toFixed(2)}% `}
                <br />
                <Typography size="sm" fontWeight="light" color="darkGray">
                  {formatToken(member.totalTokens, {
                    decimals: collective.tokens[0].decimals
                  })}{' '}
                  {collective.tokens[0].symbol}
                </Typography>
              </Typography>
            </>
          )}
        </div>

        <div className="flex border-t border-gray-200 divide-x divide-gray-200">
          {platformUser ? (
            <Typography
              href={`/profiles/${platformUser.id}`}
              size="sm"
              color="darkGray"
              className="flex-1 flex items-center justify-center py-2 hover:text-gray-500"
            >
              <FontAwesomeIcon
                icon={faUser}
                className="w-5 h-5 text-gray-400"
              />
              <span className="ml-2">View</span>
            </Typography>
          ) : (
            <Typography
              href={getEtherscanUrl(
                collective.network,
                'address',
                user.address
              )}
              size="sm"
              color="darkGray"
              className="flex-1 flex items-center justify-center py-2 hover:text-gray-500"
            >
              <FontAwesomeIcon
                icon={faExternalLink}
                className="w-5 h-5 text-gray-400"
              />
              <span className="ml-2">View on Etherscan</span>
            </Typography>
          )}
        </div>
      </div>
    </div>
  )
}

export default CollectiveMemberListItem
